import * as React from 'react';
import {View, Animated} from 'react-native';
import {datePipe} from '../../../pipes/date';
import {formatUsc} from '../../../pipes';
import {ProfileCardLogo} from '../../../../assets/images';
import {images} from '../../../../assets/icons';
import {Text} from '../../text';
import {Image} from '../../image';
import {useStyles} from '../../../hooks/styles';
import {stylePatterns} from '../../../theme/patterns';
import {VisImpStatus} from '../../../models/visuallyImpairedSettings';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../../redux/selectors/runtime/theme';
import {useTheme} from '../../../theme';

const detailsVisibilityAnimationDuration = 500;
const detailsVisibilityAnimationEasing = undefined;
const northLogoSize = {width: 87, height: 39};
const cardDeerSize = {width: 329, height: 206};

interface Props {
  card?: string;
  dateUntil?: string;
}

export const CardInfoCard: React.FC<Props> = ({dateUntil, card}) => {
  const theme = useTheme();
  const themeSettings = useSelector(themeSelector());
  const isVisImpActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const styles = useStyles(() => ({
    card: {
      ...stylePatterns.flexCenterFull,
      width: '100%',
      height: '100%',
      paddingHorizontal: 10,
      paddingVertical: 10,
    },
    cardUscLogoWrap: {
      position: 'absolute',
      top: 16,
      left: 14,
    },
    cardLeftTracery: {
      position: 'absolute',
      left: 15,
      bottom: 0,
    },
    cardNorthSlogan: {
      position: 'absolute',
      top: 16,
      right: 14,
    },
    cardNumberContainer: {
      marginTop: 43,
      zIndex: 0,
      height: theme.profileCard.cardNumber.lineHeight,
      width: '100%',
      position: 'relative',
    },
    cardNumber: {
      ...theme.profileCard.cardNumber,
      color: theme.color.white,
      textAlign: 'right',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      paddingHorizontal: 4,
    },
    cardExpiresWrap: {
      ...stylePatterns.flexCenterVertical,
      paddingTop: 3,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
    cardExpiresLabel: {
      ...theme.fonts.small,
    },
    cardExpiresValue: {
      ...theme.fonts.bodySmallLight,
      marginLeft: 10,
      minWidth: 92,
      textAlign: 'right',
    },
    detailsSwitch: {
      ...theme.fonts.bodySmall,
      color: theme.color.white,
      position: 'absolute',
      bottom: 10,
      right: 19,
    },
    datePlaceholder: {
      position: 'absolute',
      left: 15,
      bottom: 0,
    },
    dateLabel: {
      ...theme.fonts.small,
      color: theme.color.white,
    },
    dateValue: {
      ...theme.fonts.bodySmallLight,
      lineHeight: isVisImpActive ? 24 : theme.fonts.bodySmallLight.lineHeight,
      color: theme.color.white,
    },
    dateContainer: {
      position: 'absolute',
      bottom: 5,
      left: isVisImpActive ? 20 : 14,
    },
    northSlogan: {
      ...northLogoSize,
      minWidth: northLogoSize.width,
      minHeight: northLogoSize.height,
    },
    cardDeer: {
      ...cardDeerSize,
      bottom: -30,
      left: 0,
      position: 'absolute',
    },
  }));

  const [details, setDetails] = React.useState(false);
  const isDetailsAnimationRunning = React.useRef(false);

  const cardNumberRotation = React.useRef(new Animated.Value(-180)).current;
  const hiddenCardNumberRotation = React.useRef(new Animated.Value(0)).current;
  const cardCircleOpacity = React.useRef(new Animated.Value(1)).current;
  const cardCircleTranslateX = React.useRef(new Animated.Value(0)).current;
  const dateContainerOpacity = React.useRef(new Animated.Value(0)).current;
  const dateContainerTranslateY = React.useRef(new Animated.Value(10)).current;

  const animateDetailsToVisibleState = React.useCallback(() => {
    isDetailsAnimationRunning.current = true;
    cardNumberRotation.setValue(-180);
    Animated.parallel([
      Animated.timing(cardNumberRotation, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(hiddenCardNumberRotation, {
        toValue: 180,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(dateContainerTranslateY, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(dateContainerOpacity, {
        toValue: 1,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(cardCircleTranslateX, {
        toValue: -30,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(cardCircleOpacity, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
    ]).start(({finished}) => {
      if (finished) {
        hiddenCardNumberRotation.setValue(-180);
      }
      isDetailsAnimationRunning.current = false;
    });
  }, []);

  const animateDetailsToHiddenState = React.useCallback(() => {
    isDetailsAnimationRunning.current = true;
    hiddenCardNumberRotation.setValue(-180);
    Animated.parallel([
      Animated.timing(cardNumberRotation, {
        toValue: 180,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(hiddenCardNumberRotation, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(dateContainerTranslateY, {
        toValue: 10,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(dateContainerOpacity, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(cardCircleTranslateX, {
        toValue: 0,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
      Animated.timing(cardCircleOpacity, {
        toValue: 1,
        duration: detailsVisibilityAnimationDuration,
        useNativeDriver: true,
        easing: detailsVisibilityAnimationEasing,
      }),
    ]).start(({finished}) => {
      if (finished) {
        cardNumberRotation.setValue(-180);
      }
      isDetailsAnimationRunning.current = false;
    });
  }, []);

  const svgUscLogoStyle = {width: 157, height: 40};

  return (
    <View style={styles.card}>
      {!isVisImpActive && (
        <View style={styles.cardDeer}>
          <images.ProfileCardDeer />
        </View>
      )}
      <View style={styles.cardUscLogoWrap}>
        <Image
          source={ProfileCardLogo as any}
          height={svgUscLogoStyle.width}
          width={svgUscLogoStyle.height}
          style={svgUscLogoStyle}
        />
      </View>
      <View style={styles.cardNorthSlogan}>
        <images.NorthSlogan {...northLogoSize} style={styles.northSlogan} />
      </View>
      <View style={styles.cardNumberContainer}>
        <Animated.Text
          style={[
            styles.cardNumber,
            {
              transform: [
                {
                  rotateX: hiddenCardNumberRotation.interpolate({
                    inputRange: [-360, 360],
                    outputRange: ['-360deg', '360deg'],
                  }),
                },
              ],
              opacity: hiddenCardNumberRotation.interpolate({
                inputRange: [-180, -90, -89, 89, 90, 180],
                outputRange: [0, 0, 1, 1, 0, 0],
              }),
            },
          ]}
          numberOfLines={1}>
          {formatUsc(card, true)}
        </Animated.Text>
        <Animated.Text
          style={[
            styles.cardNumber,
            {
              transform: [
                {
                  rotateX: cardNumberRotation.interpolate({
                    inputRange: [-360, 360],
                    outputRange: ['-360deg', '360deg'],
                  }),
                },
              ],
              opacity: cardNumberRotation.interpolate({
                inputRange: [-180, -90, -89, 89, 90, 180],
                outputRange: [0, 0, 1, 1, 0, 0],
              }),
            },
          ]}
          numberOfLines={1}>
          {formatUsc(card, false)}
        </Animated.Text>
      </View>
      <Animated.View
        style={[
          styles.dateContainer,
          {transform: [{translateY: dateContainerTranslateY}], opacity: dateContainerOpacity},
        ]}>
        <Text style={styles.dateLabel}>Дата выдачи</Text>
        <Text style={styles.dateValue}>{datePipe(dateUntil)}</Text>
      </Animated.View>
      <Text
        testID='card-info-change-visibility-id'
        style={styles.detailsSwitch}
        onPress={() => {
          if (!isDetailsAnimationRunning.current) {
            if (!details) {
              animateDetailsToVisibleState();
            } else {
              animateDetailsToHiddenState();
            }
            setDetails(!details);
          }
        }}>
        {details ? (!isVisImpActive ? 'Скрыть данные' : 'Скрыть') : 'Показать данные'}
      </Text>
    </View>
  );
};
